import { useEffect } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Calendar,
  ClipboardList,
  Droplets,
  Gauge,
  HeartPulse,
  Pencil,
  Ruler,
  Scale,
  Stethoscope,
  Thermometer,
  Trash2,
  User,
  Wind,
  X,
} from 'lucide-react';
import type { VitalSigns } from '../../services/httpServices/vitalsService';
import { joinFullName } from '../../utils/patientName';

interface VitalsDetailsModalProps {
  record: VitalSigns | null;
  onClose: () => void;
  onEdit?: (record: VitalSigns) => void;
  onDelete?: (record: VitalSigns) => void;
}

type Tone = 'default' | 'warning' | 'danger';

/** Small icon + label + value tile used by the vitals grid. */
function VitalTile({
  icon: Icon,
  label,
  value,
  unit,
  tone = 'default',
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: string | number | null | undefined;
  unit?: string;
  tone?: Tone;
  hint?: string;
}) {
  const hasValue = value !== null && value !== undefined && value !== '';
  const valueTone =
    tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : 'text-text';
  const iconTone =
    tone === 'danger'
      ? 'bg-danger/10 text-danger'
      : tone === 'warning'
        ? 'bg-warning/10 text-warning'
        : 'bg-primary/10 text-primary';

  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-border bg-background p-2.5">
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconTone}`}>
        <Icon size={15} />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-medium uppercase tracking-wide text-text-muted">{label}</p>
        <p className={`text-sm font-semibold ${valueTone}`}>
          {hasValue ? (
            <>
              {value}
              {unit && <span className="ml-0.5 text-[11px] font-normal text-text-muted">{unit}</span>}
            </>
          ) : (
            <span className="font-normal text-text-muted">—</span>
          )}
        </p>
        {hint && <p className="text-[10px] text-text-muted/80">{hint}</p>}
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border py-1.5 last:border-0 last:pb-0">
      <span className="shrink-0 text-xs text-text-muted">{label}</span>
      <span className="text-right text-sm font-medium text-text break-words">{value}</span>
    </div>
  );
}

export function VitalsDetailsModal({ record, onClose, onEdit, onDelete }: VitalsDetailsModalProps) {
  useEffect(() => {
    if (!record) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [record, onClose]);

  if (!record) return null;

  const patient = record.patient;
  const fullName = joinFullName(patient || {}) || 'Unknown patient';

  const [systolic, diastolic] = String(record.bloodPressure || '')
    .split('/')
    .map((part) => part.trim());

  const numeric = (value: number | null | undefined) =>
    value === null || value === undefined ? null : Number(value);

  const temperature = numeric(record.temperature);
  const pulse = numeric(record.pulse);
  const spo2 = numeric(record.spo2);
  const respiratoryRate = numeric(record.respiratoryRate);
  const weight = numeric(record.weight);
  const height = numeric(record.height);
  const bmi = numeric(record.bmi);
  const painScore = numeric(record.painScore);
  const systolicValue = systolic ? Number(systolic) : null;
  const diastolicValue = diastolic ? Number(diastolic) : null;

  const temperatureTone: Tone =
    temperature === null
      ? 'default'
      : temperature >= 38 || temperature < 35.5
        ? 'danger'
        : temperature > 37.5
          ? 'warning'
          : 'default';

  const pulseTone: Tone =
    pulse === null
      ? 'default'
      : pulse > 120 || pulse < 50
        ? 'danger'
        : pulse > 100 || pulse < 60
          ? 'warning'
          : 'default';

  const spo2Tone: Tone =
    spo2 === null ? 'default' : spo2 < 90 ? 'danger' : spo2 < 95 ? 'warning' : 'default';

  const respiratoryTone: Tone =
    respiratoryRate === null
      ? 'default'
      : respiratoryRate > 30 || respiratoryRate < 8
        ? 'danger'
        : respiratoryRate > 20 || respiratoryRate < 12
          ? 'warning'
          : 'default';

  const bpTone: Tone =
    systolicValue === null || diastolicValue === null
      ? 'default'
      : systolicValue >= 180 || diastolicValue >= 120 || systolicValue < 80 || diastolicValue < 50
        ? 'danger'
        : systolicValue >= 140 || diastolicValue >= 90 || systolicValue < 90 || diastolicValue < 60
          ? 'warning'
          : 'default';

  const painTone: Tone =
    painScore === null ? 'default' : painScore >= 7 ? 'danger' : painScore >= 4 ? 'warning' : 'default';

  const ageLabel = patient?.age ? `${patient.age} yrs` : null;
  const genderLabel = patient?.gender
    ? patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1).toLowerCase()
    : null;
  // Use the visitor's local timezone so the modal matches the table's timestamp.
  const recordedAt = record.createdAt ? new Date(record.createdAt) : null;
  const recordedAtLabel = recordedAt
    ? `${recordedAt.toLocaleDateString([], {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })} · ${recordedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : '—';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Vitals for ${fullName}`}
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border p-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <User size={18} className="text-primary" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <h2 className="truncate text-base font-semibold text-text">{fullName}</h2>
                {genderLabel && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                    {genderLabel}
                  </span>
                )}
                {ageLabel && (
                  <span className="rounded-full bg-background px-2 py-0.5 text-[10px] font-medium text-text-muted">
                    {ageLabel}
                  </span>
                )}
                {patient?.bloodGroup && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-danger/10 px-2 py-0.5 text-[10px] font-medium text-danger">
                    <Droplets size={10} />
                    {patient.bloodGroup}
                  </span>
                )}
              </div>
              <p className="mt-0.5 truncate text-xs font-mono text-text-muted">
                {patient?.registrationNumber || '—'}
                {patient?.mobileNumber ? ` · ${patient.mobileNumber}` : ''}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-text-muted transition-colors hover:bg-background"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 overflow-y-auto p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <HeartPulse size={14} className="text-primary" />
              <h3 className="text-sm font-semibold text-text">Vitals</h3>
            </div>
            <span className="inline-flex items-center gap-1 text-xs text-text-muted">
              <Calendar size={12} />
              {recordedAtLabel}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            <VitalTile
              icon={Thermometer}
              label="Temperature"
              value={temperature}
              unit="°C"
              tone={temperatureTone}
            />
            <VitalTile
              icon={Activity}
              label="Blood Pressure"
              value={systolic && diastolic ? `${systolic}/${diastolic}` : null}
              unit="mmHg"
              tone={bpTone}
            />
            <VitalTile icon={HeartPulse} label="Pulse" value={pulse} unit="bpm" tone={pulseTone} />
            <VitalTile icon={Droplets} label="SpO₂" value={spo2} unit="%" tone={spo2Tone} />
            <VitalTile
              icon={Wind}
              label="Resp. Rate"
              value={respiratoryRate}
              unit="/min"
              tone={respiratoryTone}
            />
            <VitalTile icon={Gauge} label="Pain Score" value={painScore} unit="/10" tone={painTone} />
            <VitalTile icon={Scale} label="Weight" value={weight} unit="kg" />
            <VitalTile icon={Ruler} label="Height" value={height} unit="cm" />
            <VitalTile icon={Stethoscope} label="BMI" value={bmi} unit="kg/m²" />
          </div>

          {(record.visit || record.recordedBy) && (
            <div className="rounded-xl border border-border bg-background p-3">
              <div className="mb-1.5 flex items-center gap-1.5">
                <ClipboardList size={13} className="text-primary" />
                <h3 className="text-xs font-semibold text-text">Context</h3>
              </div>
              <DetailRow label="Visit" value={record.visit?.visitNumber || '—'} />
              <DetailRow
                label="Visit type"
                value={record.visit?.type ? record.visit.type.replace(/_/g, ' ') : '—'}
              />
              <DetailRow label="Recorded by" value={record.recordedBy?.name || 'Not recorded'} />
            </div>
          )}

          {record.notes && (
            <div className="rounded-xl border border-border bg-background p-3">
              <h3 className="mb-1 text-xs font-semibold text-text">Notes</h3>
              <p className="whitespace-pre-wrap text-sm text-text-muted">{record.notes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-border p-4">
          <div>
            {onDelete && (
              <button
                type="button"
                onClick={() => onDelete(record)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-danger/30 px-3 py-1.5 text-sm font-medium text-danger transition-colors hover:bg-danger/10"
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-3 py-1.5 text-sm text-text-muted transition-colors hover:text-text"
            >
              Close
            </button>
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(record)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-dark"
              >
                <Pencil size={14} />
                Edit Vitals
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
